import { UIColor } from "uicore-ts"
import { RHPublishedProcurementLifecycleStateText } from "../../../../../models/PublishedProcurementSearch"
import {
    RHOffererSubmissionOutcome,
    RHProcurementInformativeStatus,
    RHProcurementLifecycleState
} from "../../../SocketClientFunctions"
import { RHColor } from "../RHColor"
import { RHProcurementStatusPresentation } from "./RHProcurementStatusPresentation"
import { RHTextSanitizer } from "./RHTextSanitizer"


/**
 * "Hanke seisundid ja filtrid v3.1": text/color for the 7 server-resolved
 * lifecycle states plus their informative sub-statuses. `lifecycleState` and
 * `procurerInformativeStatus`/`offererInformativeStatus` are computed exactly
 * once, server-side, by ProcurementLifecycleService — this class only maps
 * that already-decided data to display text/color, never re-derives it.
 *
 * The buyer dashboard, supplier dashboard, global search, and procurement-
 * details header all call this class so their lifecycle-state wording cannot
 * disagree. RHProcurementStatusPresentation remains responsible for legacy
 * presentation consumers and shared informative-text formatting.
 */
export interface RHProcurementLifecycleRowPresentation {
    /** Bold main line — always the lifecycle state text. */
    mainText: string
    /** Italic second line — at most one, per spec. Absent when nothing applies. */
    secondaryText?: string
    /** Optional semantic colour for the second line; the main line keeps `color`. */
    secondaryColor?: UIColor
    color: UIColor
    linksToOffers: boolean
}



const lifecycleStateColor: Record<RHProcurementLifecycleState, UIColor> = {
    preparing: RHColor.textBlack,
    started: RHColor.textBlack,
    evaluating: RHColor.textBlack,
    awaitingContract: RHColor.textBlack,
    closedWithoutContract: RHColor.textBlack,
    inExecution: RHColor.textBlack,
    executed: RHColor.textBlack,
    cancelled: RHColor.textBlack
}

const positiveOffererSubmissionOutcomes = new Set<RHOffererSubmissionOutcome>([
    "winner",
    "partialWinner",
    "multiplePartsWinner",
    "partContractSigned",
    "partContractsSigned",
    "contractSigned"
])


export class RHProcurementLifecyclePresentation {

    /**
     * Composes the two-line status cell markup the spec requires: the bold
     * main state on the first line, an italic informative status (when any)
     * on the second. Cell text renders as HTML (see formatRHDateTimeWithLineBreak
     * for the same mechanism already in production), so both lines are
     * entity-escaped first.
     */
    static twoLineCellHTML(mainText: string, secondaryText?: string, secondaryColor?: UIColor): string {
        const escapedMainText = `<b>${RHTextSanitizer.escapedHTML(mainText)}</b>`
        if (!secondaryText) {
            return escapedMainText
        }
        const escapedSecondaryText = RHTextSanitizer.escapedHTML(secondaryText)
        if (secondaryColor) {
            return `${escapedMainText}<br><i style="color: ${secondaryColor.stringValue}">${escapedSecondaryText}</i>`
        }
        return `${escapedMainText}<br><i>${escapedSecondaryText}</i>`
    }


    static textForLifecycleState(state: RHProcurementLifecycleState): string {
        return RHPublishedProcurementLifecycleStateText[state]
    }


    static detailsTextForLifecycleState(
        state: RHProcurementLifecycleState,
        informativeStatusText?: string
    ): string {
        const detailsStatusText = informativeStatusText
        if (!detailsStatusText) {
            return RHProcurementLifecyclePresentation.textForLifecycleState(state)
        }

        const parenthesizedStatusText =
            detailsStatusText.charAt(0).toLocaleLowerCase("et-EE") + detailsStatusText.slice(1)
        return `${RHProcurementLifecyclePresentation.textForLifecycleState(state)} (${parenthesizedStatusText})`
    }


    static colorForLifecycleState(state: RHProcurementLifecycleState): UIColor {
        return lifecycleStateColor[state]
    }


    /** "Lõpetatud" filter chip and "Lõpetatud" main-line text both cover these two states. */
    static isLifecycleStateClosed(state: RHProcurementLifecycleState): boolean {
        return state === "awaitingContract" || state === "closedWithoutContract"
    }


    static presentationForProcurerRow(row: {
        lifecycleState?: RHProcurementLifecycleState
        procurerInformativeStatus?: RHProcurementInformativeStatus
    }): RHProcurementLifecycleRowPresentation {
        const state = row.lifecycleState ?? "preparing"
        const informativeStatus = row.procurerInformativeStatus

        let secondaryText: string | undefined
        let secondaryColor: UIColor | undefined
        if (informativeStatus?.kind === "deadlineCountdown") {
            // The deadline can tick past zero while this row's snapshot is
            // still on screen — the periodic repaint only re-renders it, it
            // never re-derives lifecycleState (see class doc). Once expired,
            // Math.floor on a negative duration would make _countdownTextForTimeLeft
            // show a bogus "-1 päeva -1 minutit", so show a transitional message
            // instead until the next real fetch replaces this stale informativeStatus.
            const timeLeftMilliseconds = new Date(informativeStatus.deadlineDate).getTime() - Date.now()
            if (timeLeftMilliseconds >= 0) {
                secondaryText = RHProcurementStatusPresentation._countdownTextForTimeLeft(timeLeftMilliseconds)
            }
            else {
                secondaryText = "Tähtaeg möödas, töötlemisel"
            }
        }
        else if (informativeStatus?.kind === "viewOffers") {
            secondaryText = "Vaata pakkumusi"
        }
        else if (informativeStatus?.kind === "pendingContracts") {
            const numberOfPendingContracts = informativeStatus.numberOfPendingContracts
            const numberOfWinningPartContracts = informativeStatus.numberOfWinningPartContracts
            secondaryText = `Lepingu ootel (${numberOfPendingContracts}/${numberOfWinningPartContracts})`
        }
        else if (informativeStatus?.kind === "contractNotConcluded") {
            secondaryText = "Lepingut ei sõlmitud"
        }
        else if (state === "awaitingContract") {
            secondaryText = "Olete valinud võitja"
            secondaryColor = RHColor.success
        }

        return {
            mainText: RHProcurementLifecyclePresentation.textForLifecycleState(state),
            secondaryText,
            secondaryColor,
            color: RHProcurementLifecyclePresentation.colorForLifecycleState(state),
            linksToOffers: informativeStatus?.kind === "viewOffers" ||
                RHProcurementLifecyclePresentation.isLifecycleStateClosed(state)
        }
    }


    static presentationForOffererRow(row: {
        lifecycleState?: RHProcurementLifecycleState
        offererInformativeStatus?: RHProcurementInformativeStatus
        offererSubmissionOutcome?: RHOffererSubmissionOutcome
    }): RHProcurementLifecycleRowPresentation {
        const state = row.lifecycleState ?? "started"
        const informativeStatus = row.offererInformativeStatus

        let secondaryText: string | undefined
        let secondaryColor: UIColor | undefined
        if (state !== "executed" && row.offererSubmissionOutcome && row.offererSubmissionOutcome !== "evaluating") {
            secondaryText = RHProcurementStatusPresentation.textForOffererSubmissionOutcome(
                row.offererSubmissionOutcome
            )
            if (positiveOffererSubmissionOutcomes.has(row.offererSubmissionOutcome)) {
                secondaryColor = RHColor.success
            }
        }
        else if (informativeStatus?.kind === "offerSubmitted" && row.offererSubmissionOutcome !== "evaluating") {
            secondaryText = "Pakkumus esitatud"
        }
        else if (informativeStatus?.kind === "offerInProgress") {
            secondaryText = "Pakkumus pooleli"
        }
        else if (informativeStatus?.kind === "registeredAsTenderer") {
            secondaryText = "Hankele registreeritud"
        }
        else if (informativeStatus?.kind === "deadlineCountdown") {
            // Same expired-deadline handling as presentationForProcurerRow above.
            const timeLeftMilliseconds = new Date(informativeStatus.deadlineDate).getTime() - Date.now()
            if (timeLeftMilliseconds >= 0) {
                secondaryText = RHProcurementStatusPresentation._countdownTextForTimeLeft(timeLeftMilliseconds)
            }
            else {
                secondaryText = "Tähtaeg möödas, töötlemisel"
            }
        }

        if (state === "executed") {
            secondaryText = undefined
            secondaryColor = undefined
        }

        // Links to "Minu pakkumus" only once the company has actually acted:
        // started/evaluating with a submitted offer, started with a draft or
        // registration in progress, or any state where the server has an outcome
        // for the acting company's registered participation.
        const hasActed =
            informativeStatus?.kind === "offerSubmitted" ||
            informativeStatus?.kind === "offerInProgress" ||
            informativeStatus?.kind === "registeredAsTenderer" ||
            row.offererSubmissionOutcome !== undefined

        return {
            mainText: RHProcurementLifecyclePresentation.textForLifecycleState(state),
            secondaryText,
            secondaryColor,
            color: RHProcurementLifecyclePresentation.colorForLifecycleState(state),
            linksToOffers: hasActed
        }
    }

}
